import type { FeatureAccessOptions } from '@blueshift-ui/auth/dist/types';
import type { Subscription } from '../../../../../types';

import * as styles from './index.css';
import {
  EventScopeProvider,
  TrackedLink,
  useAnalytics,
  useEventScope,
} from '@blueshift-ui/analytics';
import { Suspense, useMemo, useState } from 'react';
import ActionGroup from '../../../../../layout/forms/action-group';
import BackToPlanButton from '../../../../navigation/back-to-plan-button';
import Box from '@blueshift-ui/core/dist/components/box';
import BreadcrumbLink from '../../../../navigation/breadcrumb-link';
import CancelPlanDialogTrigger from '../../dialogs/cancel-plan/trigger';
import ConfirmUpdateButton from '../../actions/confirm-update';
import EVENT_NAMES from '../../../../../constants/generated/event-names';
import RichTranslation from '@blueshift-ui/i18n/dist/components/rich-translation';
import SubscriptionAction from './subscription-action';
import SubscriptionDetails from '../subscription-details';
import Typography from '@blueshift-ui/theme/dist/components/typography';
import UpdateConfirmation from './update-confirmation';
import UpdateDiscard from '../update-discard';
import UpdateMembershipStep1 from '../../forms/step-1';
import UpdateMembershipStep2 from '../../forms/step-2';
import applyHourFilter from './helpers/apply-hour-filter';
import useFeatureAccess from '@blueshift-ui/auth/dist/hooks/use-feature-access';
import useRichTranslation from '@blueshift-ui/i18n/dist/hooks/use-rich-translation';
import useTranslation from '@blueshift-ui/i18n/dist/hooks/use-translation';

enum Step {
  one = 1,
  two = 2,
  discard = 3,
  confirmation = 4,
}

interface UpdateMembershipOptionsProps {
  subscription: Subscription;
}

const FILTER_2_HOUR_MEMBERSHIP_FLAG: FeatureAccessOptions = {
  flag: 'DISCO-168-2-hour-membership',
};

function UpdateMembershipOptions({
  subscription: initialSubscription,
}: UpdateMembershipOptionsProps) {
  const analytics = useAnalytics();
  const eventScope = useEventScope();

  // DISCO-168-changes in My program update page
  // if filter2hourMembership feature flag is enabled, remove 2 hour membership option
  const { hasAccess: filter2hourMembership } = useFeatureAccess({
    criteria: FILTER_2_HOUR_MEMBERSHIP_FLAG,
  });

  const subscription = useMemo(() => {
    return applyHourFilter(initialSubscription, filter2hourMembership ?? false);
  }, [initialSubscription, filter2hourMembership]);

  const { translate: richTranslate } = useRichTranslation('update.membership.options');
  const { translate } = useTranslation('update.membership.options');

  const [selectedSubscription, setSelectedSubscription] = useState<Subscription>(subscription);
  const [step, setStep] = useState(Step.one);
  const [subscriptionAction, setSubscriptionAction] = useState(SubscriptionAction.change);
  const [subscriptionOptions, setSubscriptionOptions] = useState<Subscription[]>();

  function getAdjustHoursOptions() {
    return subscription?.upgrade_downgrade_options?.hour_change_options ?? [];
  }

  function getContractPlanOptions() {
    return (
      subscription?.upgrade_downgrade_options?.plan_change_options?.filter(
        ({ contract: { duration_periods } }) => duration_periods !== 1
      ) || []
    );
  }

  function getMonthlyPlanOptions() {
    return (
      subscription?.upgrade_downgrade_options?.plan_change_options?.filter(
        ({ contract: { duration_periods } }) => duration_periods === 1
      ) || []
    );
  }

  function handleBackLinkClick() {
    // reset to current subscription
    setSelectedSubscription(subscription);
    setStep(Step.one);
  }

  function handleConfirmClick() {
    setStep(Step.confirmation);
  }

  function handleStep1Click(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
    const buttonName = (event.target as HTMLElement).textContent || undefined;
    let options: Subscription[] = [subscription];

    switch (buttonName) {
      case translate('adjust_button_name'):
        options = options.concat(getAdjustHoursOptions());
        setSubscriptionAction(SubscriptionAction.adjust);
        break;
      case translate('contract_plan_button_name'):
        options = options.concat(getContractPlanOptions());
        setSubscriptionAction(SubscriptionAction.change);
        break;
      case translate('monthly_plan_button_name'):
        options = options.concat(getMonthlyPlanOptions());
        setSubscriptionAction(SubscriptionAction.change);
        break;
    }

    setSubscriptionOptions(options);
    setStep(Step.two);
  }

  function handleStep2Change(subscriptionOption: Subscription) {
    setSelectedSubscription(subscriptionOption);

    // TODO: replace this with a TrackedRadioButtonGroup in UpdateMembershipStep2
    analytics.track(EVENT_NAMES.elementClicked, {
      element_id: `membership_option_${subscriptionOption.item_id}`,
      element_name: 'update_membership_option',
      metadata: {
        option_id: subscriptionOption.item_id,
        option_name: subscriptionOption.name,
      },
      page_path: window.location.pathname,
      page_section: eventScope?.pageSection,
    });
  }

  if (step === Step.discard) {
    return (
      <EventScopeProvider eventScope={{ pageSection: 'update_membership_plan_discard' }}>
        <BackToPlanButton />
        <UpdateDiscard />
      </EventScopeProvider>
    );
  }

  if (step === Step.confirmation) {
    return (
      <EventScopeProvider eventScope={{ pageSection: 'update_membership_plan_confirmation' }}>
        <UpdateConfirmation />
      </EventScopeProvider>
    );
  }

  return (
    <>
      {step === Step.two ? (
        <BreadcrumbLink id="back_to_step_1_button" onClick={handleBackLinkClick}>
          {translate('back_to_step_1', '')}
        </BreadcrumbLink>
      ) : (
        <BackToPlanButton />
      )}

      <Typography component="h1" variant="subtitle1">
        {translate('title', '')}
      </Typography>

      <RichTranslation i18nKey="content" translator={richTranslate} />

      <RichTranslation
        i18nKey={subscription.id === selectedSubscription.id ? 'current_plan' : 'updated_plan'}
        translator={richTranslate}
      />

      <SubscriptionDetails subscription={selectedSubscription} />

      <Box className={styles.ctaContainer}>
        {step === Step.two && subscriptionOptions ? (
          <UpdateMembershipStep2
            onChange={handleStep2Change}
            subscriptionAction={subscriptionAction}
            subscriptionOptions={subscriptionOptions}
          />
        ) : (
          <UpdateMembershipStep1
            isContract={!!subscription.is_contract}
            onClick={handleStep1Click}
          />
        )}
      </Box>

      {step === Step.two && subscriptionOptions && (
        <ActionGroup>
          <ConfirmUpdateButton
            currentSubscription={subscription}
            newSubscription={selectedSubscription}
            onResolved={handleConfirmClick}
          />
          <TrackedLink id="discard_changes_link" onClick={() => setStep(Step.discard)}>
            {translate('discard_link_name', '')}
          </TrackedLink>
        </ActionGroup>
      )}

      <Suspense>
        <CancelPlanDialogTrigger subscription={subscription}>
          {translate('cancel_paid_plan')}
        </CancelPlanDialogTrigger>
      </Suspense>
    </>
  );
}

export default UpdateMembershipOptions;
